import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  DateRangePanel,
  type DateRangePreset,
  type DateRangeValue,
} from '@clockwise/ui';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface FilterOption {
  value: string;
  label: string;
  /** Color class for a dot indicator (e.g. "bg-green-500") */
  color?: string;
  /** Icon rendered next to the option label */
  icon?: React.ComponentType<{ className?: string }>;
  /** Count shown next to the option */
  count?: number;
}

export type FilterFieldType =
  | 'multi-select'
  | 'single-select'
  | 'text'
  | 'number'
  | 'date'
  | 'date-range';

export interface FilterDefinition {
  key: string;
  label: string;
  type: FilterFieldType;
  /** Icon rendered on the chip and in the "add filter" menu */
  icon?: React.ComponentType<{ className?: string }>;
  /** Options for "multi-select" / "single-select" types */
  options?: FilterOption[];
  /** Operators for text/number/date. Defaults provided per type if omitted. */
  operators?: { value: string; label: string }[];
  /**
   * For `type: 'date-range'` — which macros the chip's popover offers, in
   * order. Defaults to the standard list; pass a subset to narrow it, or
   * include the opt-in `all_time` / `current_week` presets to offer them.
   */
  presets?: readonly DateRangePreset[];
  /** Placeholder text for input-based filters */
  placeholder?: string;
  /**
   * When `false`, the chip omits its remove (×) button and cannot be
   * auto-removed on empty close. Use for a "sticky default" filter the
   * parent always seeds into FilterState (e.g. a status filter that
   * defaults to Active) so the × doesn't masquerade as "clear" when it
   * would really just snap back to the default. Defaults to `true`.
   */
  removable?: boolean;
  /**
   * Optional custom popover content for filters that need richer UI than
   * the built-in editors (e.g. an AssigneePicker with avatars, group
   * headings, role names). For a plain mutually-exclusive choice prefer
   * `type: 'single-select'` — it owns the swap and chip display for you.
   *
   * The callback receives the current selection as `string[]` (the
   * multi-select wire shape) plus toggle/clear/close handlers wired into
   * the same FilterBar state machine the default editor uses.
   *
   * NOTE: `onToggle` mutates the multi-select array — `onToggle(v, true)`
   * appends, `onToggle(v, false)` removes. Two `onToggle` calls in one
   * event both read the same render-time array, so you cannot "swap" by
   * clearing then setting in the same tick (both see the pre-toggle
   * array; last write wins → both values present). For single-value
   * behavior use `type: 'single-select'` instead of emulating it here.
   *
   * ```tsx
   * customEditor: ({ selected, onToggle, onClose }) => (
   *   <Picker
   *     active={selected}
   *     onToggle={(v, on) => onToggle(v, on)}
   *     onClose={onClose}
   *   />
   * )
   * ```
   */
  customEditor?: (props: {
    selected: string[];
    onToggle: (value: string, checked: boolean) => void;
    onClear: () => void;
    onClose: () => void;
  }) => React.ReactNode;
  /**
   * Optional custom width for the popover content (Tailwind class).
   * Defaults to `w-56` (matches the default editor). Custom editors
   * with richer rows usually want more room — e.g. `w-[280px]`.
   */
  popoverWidthClass?: string;
}

export type FilterValue =
  | { type: 'multi-select'; values: string[] }
  | { type: 'single-select'; value: string }
  | { type: 'input'; operator: string; value: string }
  /**
   * A date range is a named macro ("previous quarter") OR two explicit bounds,
   * never a third thing — the same `DateRangeValue` model the standalone
   * date-range controls use. `preset` is the load-bearing field: it is stored
   * instead of the dates it resolves to, so a bookmarked "previous quarter"
   * still means the quarter before *today* when the page is reopened next
   * month. `from`/`to` carry values only for `preset: 'custom'`, using `''`
   * for "unset" to match the other scalar variants.
   */
  | { type: 'date-range'; preset: DateRangePreset; from: string; to: string };

/** Filter state: mapping from filter key to its current value */
export type FilterState = Record<string, FilterValue>;

export interface FilterBarProps {
  /** Available filter definitions */
  definitions: FilterDefinition[];
  /** Current filter state */
  value: FilterState;
  /** Called when filters change */
  onChange: (state: FilterState) => void;
  /** Optional className for the wrapper */
  className?: string;
  /**
   * Content rendered before the filter chips (e.g. a search input).
   * A vertical separator is automatically inserted between children and chips.
   */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Default operators per type
// ---------------------------------------------------------------------------

const DEFAULT_NUMBER_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'gte', label: 'at least' },
  { value: 'lte', label: 'at most' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
];

const DEFAULT_DATE_OPERATORS = [
  { value: 'eq', label: 'on' },
  { value: 'gte', label: 'after' },
  { value: 'lte', label: 'before' },
];

const DEFAULT_TEXT_OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'eq', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
];

/**
 * True iff `v` carries a non-empty payload — a multi-select with at
 * least one chosen option, a date-range that names a window, or a
 * scalar with a non-blank value. Centralized here so every consumer
 * (toolbar dot indicators, chip rendering, future clear-all wiring)
 * shares one definition; if the FilterValue union grows a 4th variant,
 * this is the one place to update.
 */
export function isActiveFilterValue(v: FilterValue): boolean {
  if (v.type === 'multi-select') return v.values.length > 0;
  // "Active" here must mean the same thing `resolveDateRange` means, or the
  // chip advertises a filter the page never applies. A named macro always
  // names a window (`all_time` names the unbounded one, which callers
  // special-case on the preset). A Custom pick only does so with BOTH bounds:
  // the calendar emits `{from, to: undefined}` on the first click of a range,
  // and treating that half-drag as active would leave a chip reading "after
  // Jun 15" and a lit has-filters dot while `resolveDateRange` returns null
  // and the query goes out unfiltered. Half-finished is not finished — the
  // empty-close rule then drops the chip, as it does for every other type.
  if (v.type === 'date-range') return v.preset !== 'custom' || (v.from !== '' && v.to !== '');
  return v.value !== '';
}

/**
 * True iff any key in `state` has an active value. O(keys) scan;
 * cheaper than a useMemo for the typical handful of filter keys.
 */
export function hasAnyActiveValue(state: FilterState): boolean {
  for (const v of Object.values(state)) {
    if (isActiveFilterValue(v)) return true;
  }
  return false;
}

function getDefaultOperators(type: FilterFieldType): { value: string; label: string }[] {
  switch (type) {
    case 'number':
      return DEFAULT_NUMBER_OPERATORS;
    case 'date':
      return DEFAULT_DATE_OPERATORS;
    case 'text':
      return DEFAULT_TEXT_OPERATORS;
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSelectedLabels(definition: FilterDefinition, selected: string[]): string {
  if (selected.length === 0) return '';
  const firstVal = selected[0] ?? '';
  if (selected.length === 1) {
    return definition.options?.find((o) => o.value === firstVal)?.label ?? firstVal;
  }
  if (selected.length <= 2) {
    return selected
      .map((v) => definition.options?.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }
  const firstLabel = definition.options?.find((o) => o.value === firstVal)?.label ?? firstVal;
  return `${firstLabel} +${selected.length - 1}`;
}

function getOperatorLabel(definition: FilterDefinition, operator: string): string {
  const operators = definition.operators ?? getDefaultOperators(definition.type);
  return operators.find((o) => o.value === operator)?.label ?? operator;
}

/** Format a date-range filter for display on the chip. */
export function formatDateRangeDisplay(from: string, to: string): string {
  const fmt = (d: string): string => {
    const [year, month, day] = d.split('-');
    if (!year || !month || !day) return d;
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const monthName = months[parseInt(month, 10) - 1] ?? month;
    return `${monthName} ${parseInt(day, 10)}`;
  };

  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `after ${fmt(from)}`;
  if (to) return `before ${fmt(to)}`;
  return '';
}

// ---------------------------------------------------------------------------
// MultiSelectEditor – checkbox list for multi-select filters
// ---------------------------------------------------------------------------

function MultiSelectEditor({
  definition,
  selected,
  onToggle,
  onClear,
  onSelectAll,
}: {
  definition: FilterDefinition;
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  onClear: () => void;
  onSelectAll: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const options = definition.options ?? [];
  // Per-instance prefix so two FilterBars sharing a definition key don't
  // collide on DOM ids, and option values with spaces/dots can't produce
  // malformed ids — we key the control id on the option index, not value.
  const uid = useId();

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div className="flex flex-col">
      {options.length > 5 && (
        <div className="px-2 pt-2 pb-1">
          <Input
            placeholder={`Search ${definition.label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
      )}

      <ScrollArea className={cn(filtered.length > 8 ? 'h-52' : '')}>
        <div className="p-1">
          {filtered.map((opt, i) => {
            const controlId = `${uid}-${i}`;
            return (
              <label
                key={opt.value}
                htmlFor={controlId}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                  // Same pale `--row-hover` lime every other dropdown option
                  // list hovers to (Combobox, Picker, Select) — not the
                  // cyan-soft wash this had drifted to.
                  selected.includes(opt.value)
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground light:hover:bg-row-hover light:hover:text-foreground',
                )}
              >
                <Checkbox
                  id={controlId}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={(c) => onToggle(opt.value, c as boolean)}
                />
                {opt.icon != null ? (
                  <opt.icon className="size-3.5 shrink-0 text-muted-foreground" />
                ) : opt.color != null ? (
                  <span className={cn('inline-block size-2 shrink-0 rounded-full', opt.color)} />
                ) : null}
                <span className="truncate text-xs">{opt.label}</span>
                {opt.count !== undefined && (
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                    {opt.count}
                  </span>
                )}
                {selected.includes(opt.value) && opt.count === undefined && (
                  <Check className="ml-auto size-3 text-primary" />
                )}
              </label>
            );
          })}
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">No options found</p>
          )}
        </div>
      </ScrollArea>

      <Separator />
      <div className="flex items-center justify-between p-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={onSelectAll}
        >
          Select all
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SingleSelectEditor – mutually-exclusive radio-style list for
// single-select filters. Picks one value and closes; the chip owns the
// "selected ↔ none" swap so consumers never touch the multi-select array.
// ---------------------------------------------------------------------------

function SingleSelectEditor({
  definition,
  selected,
  onSelect,
}: {
  definition: FilterDefinition;
  selected: string;
  onSelect: (value: string) => void;
}): React.ReactElement {
  const options = definition.options ?? [];
  return (
    <div className="flex flex-col p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
            // Same pale `--row-hover` lime every other dropdown option list
            // hovers to (Combobox, Picker, Select) — not the cyan-soft wash
            // this had drifted to.
            selected === opt.value
              ? 'bg-accent text-accent-foreground'
              : 'hover:bg-accent hover:text-accent-foreground light:hover:bg-row-hover light:hover:text-foreground',
          )}
        >
          {opt.icon != null ? (
            <opt.icon className="size-3.5 shrink-0 text-muted-foreground" />
          ) : opt.color != null ? (
            <span className={cn('inline-block size-2 shrink-0 rounded-full', opt.color)} />
          ) : null}
          <span className="truncate text-xs">{opt.label}</span>
          {selected === opt.value && <Check className="ml-auto size-3 text-primary" />}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InputEditor – operator + value input for text/number/date filters
// ---------------------------------------------------------------------------

function InputEditor({
  definition,
  operator,
  value,
  onApply,
}: {
  definition: FilterDefinition;
  operator: string;
  value: string;
  onApply: (operator: string, value: string) => void;
}): React.ReactElement {
  const operators = definition.operators ?? getDefaultOperators(definition.type);
  const [localOp, setLocalOp] = useState(operator || operators[0]?.value || 'eq');
  const [localValue, setLocalValue] = useState(value);

  const inputType = definition.type === 'date' ? 'date' : 'text';

  const handleApply = (): void => {
    if (localValue === '') return;
    onApply(localOp, localValue);
  };

  return (
    <div className="flex flex-col gap-2 p-3">
      {operators.length > 1 && (
        <Select value={localOp} onValueChange={setLocalOp}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {operators.map((op) => (
              <SelectItem key={op.value} value={op.value} className="text-xs">
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        type={inputType}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={definition.placeholder ?? `Enter ${definition.label.toLowerCase()}...`}
        className="h-8 text-xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleApply();
        }}
      />
      <Button size="sm" className="h-7 w-full text-xs" onClick={handleApply}>
        Apply
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateRangeEditor – preset sidebar + range calendar for date-range filters
// ---------------------------------------------------------------------------

/**
 * Bridges the chip's `FilterValue` to the shared `DateRangeValue` the
 * date-range widgets speak. The two differ only in how "unset" is spelled —
 * `''` here (matching the other scalar chip variants) versus `undefined`
 * there — so this is a pure shape adapter, not a second model.
 *
 * The editor body itself is `DateRangePanel`, the same preset-sidebar +
 * two-month calendar the standalone `DateRangePopover` renders. Sharing it is
 * the point of this component: the previous editor was two bare `<input
 * type="date">` boxes with no idea macros existed, so a filter chip could only
 * ever read "Jan 1 – Mar 1" where the rest of the app said "This quarter".
 */
function DateRangeEditor({
  value,
  presets,
  onChange,
  onClose,
}: {
  value: Extract<FilterValue, { type: 'date-range' }>;
  presets: readonly DateRangePreset[];
  onChange: (next: Extract<FilterValue, { type: 'date-range' }>) => void;
  onClose: () => void;
}): React.ReactElement {
  return (
    <DateRangePanel
      value={{
        preset: value.preset,
        from: value.from || undefined,
        to: value.to || undefined,
      }}
      presets={presets}
      onChange={(next: DateRangeValue) =>
        onChange({
          type: 'date-range',
          preset: next.preset,
          from: next.from ?? '',
          to: next.to ?? '',
        })
      }
      // A macro is a complete answer, so the panel closes on the pick. Custom
      // is not — the user still has to click the range's two ends — so Custom
      // never auto-closes. Only a finished range reaches `onChange` (the panel
      // holds the first click itself), so clicking away mid-pick leaves the
      // chip's value as it was.
      onPresetPicked={(_, shouldClose) => {
        if (shouldClose) onClose();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// ActiveFilterChip – clickable chip for an active filter
// ---------------------------------------------------------------------------

function ActiveFilterChip({
  definition,
  filterValue,
  onUpdate,
  onRemove,
  removable = true,
  defaultOpen = false,
}: {
  definition: FilterDefinition;
  filterValue: FilterValue;
  onUpdate: (val: FilterValue) => void;
  onRemove: () => void;
  removable?: boolean;
  defaultOpen?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  // Auto-open the value popover when this chip is freshly added via "Add filter".
  useEffect(() => {
    if (defaultOpen) {
      const t = setTimeout(() => setOpen(true), 60);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount
  const Icon = definition.icon;

  let displayText = '';
  let connectorText = 'is';

  if (filterValue.type === 'multi-select') {
    displayText = getSelectedLabels(definition, filterValue.values);
  } else if (filterValue.type === 'single-select') {
    displayText =
      definition.options?.find((o) => o.value === filterValue.value)?.label ?? filterValue.value;
  } else if (filterValue.type === 'date-range') {
    connectorText = '';
    // A macro reads as its name; only a Custom pick has no name to show, so it
    // reads as its dates. `formatDateRangeDisplay` still handles the one-sided
    // case ("after Jun 15"), which a URL can carry even though the calendar
    // panel only produces whole ranges.
    displayText =
      filterValue.preset === 'custom'
        ? formatDateRangeDisplay(filterValue.from, filterValue.to)
        : DATE_RANGE_PRESET_LABELS[filterValue.preset];
  } else {
    connectorText = getOperatorLabel(definition, filterValue.operator);
    displayText = filterValue.value;
  }

  const handleMultiToggle = (optValue: string, checked: boolean): void => {
    if (filterValue.type !== 'multi-select') return;
    const current = filterValue.values;
    const next = checked ? [...current, optValue] : current.filter((v) => v !== optValue);
    onUpdate({ type: 'multi-select', values: next });
  };

  const handleSingleSelect = (value: string): void => {
    onUpdate({ type: 'single-select', value });
    setOpen(false);
  };

  const handleMultiClear = (): void => {
    onUpdate({ type: 'multi-select', values: [] });
  };

  const handleMultiSelectAll = (): void => {
    const all = definition.options?.map((o) => o.value) ?? [];
    onUpdate({ type: 'multi-select', values: all });
  };

  const handleInputApply = (operator: string, value: string): void => {
    onUpdate({ type: 'input', operator, value });
    setOpen(false);
  };

  const handleDateRangeChange = (next: Extract<FilterValue, { type: 'date-range' }>): void => {
    onUpdate(next);
  };

  // Auto-remove the chip when the popover closes with no committed
  // values. Symmetric to the auto-open above: a freshly-added chip
  // that the user closes without picking anything disappears, and a
  // user who clears every value off an established chip and clicks
  // away gets the same outcome — "a filter without values is
  // meaningless." Matches Linear/Airtable behavior.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next && removable && !isActiveFilterValue(filterValue)) {
      onRemove();
    }
  };

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-9 items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium transition-colors',
              'hover:bg-muted/50',
              'border-border',
              open && 'ring-1 ring-ring',
            )}
          >
            {Icon != null && <Icon className="size-3 text-muted-foreground" />}
            <span className="text-muted-foreground">{definition.label}</span>
            {connectorText && <span className="text-muted-foreground">{connectorText}</span>}
            <span className="max-w-[150px] truncate font-semibold">{displayText}</span>
            <ChevronDown className="size-3 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          // Only the date-range editor is a calendar, and only a calendar takes
          // the picker's panel geometry (12px, deeper shadow). The list-shaped
          // editors stay on the overlay radius with every other menu.
          data-picker={definition.type === 'date-range' ? 'date' : undefined}
          className={cn(
            'p-0',
            // The date-range panel is a two-month calendar beside a preset
            // rail — it sizes itself and would be crushed by the w-56 the
            // list-shaped editors want.
            definition.popoverWidthClass ?? (definition.type === 'date-range' ? 'w-auto' : 'w-56'),
          )}
        >
          {definition.customEditor ? (
            // Custom editor sees the multi-select-shaped selection.
            // Toggle/clear flow through the same state machine the
            // default editor uses.
            definition.customEditor({
              selected: filterValue.type === 'multi-select' ? filterValue.values : [],
              onToggle: handleMultiToggle,
              onClear: handleMultiClear,
              onClose: () => setOpen(false),
            })
          ) : filterValue.type === 'single-select' ? (
            <SingleSelectEditor
              definition={definition}
              selected={filterValue.value}
              onSelect={handleSingleSelect}
            />
          ) : filterValue.type === 'multi-select' ? (
            <MultiSelectEditor
              definition={definition}
              selected={filterValue.values}
              onToggle={handleMultiToggle}
              onClear={handleMultiClear}
              onSelectAll={handleMultiSelectAll}
            />
          ) : filterValue.type === 'date-range' ? (
            <DateRangeEditor
              value={filterValue}
              presets={definition.presets ?? DATE_RANGE_PRESETS}
              onChange={handleDateRangeChange}
              onClose={() => setOpen(false)}
            />
          ) : (
            <InputEditor
              definition={definition}
              operator={filterValue.operator}
              value={filterValue.value}
              onApply={handleInputApply}
            />
          )}
        </PopoverContent>
      </Popover>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          aria-label={`Remove ${definition.label} filter`}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddFilterButton – the "+ Add filter" command-menu popover
// ---------------------------------------------------------------------------

function AddFilterButton({
  definitions,
  activeKeys,
  onAdd,
}: {
  definitions: FilterDefinition[];
  activeKeys: Set<string>;
  onAdd: (key: string) => void;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const available = definitions.filter((d) => !activeKeys.has(d.key));

  if (available.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 border border-dashed text-muted-foreground"
        >
          <Plus className="size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-0">
        <Command>
          <CommandInput ref={inputRef} placeholder="Search filters..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>No filters found.</CommandEmpty>
            <CommandGroup>
              {available.map((def) => {
                const DefIcon = def.icon;
                return (
                  <CommandItem
                    key={def.key}
                    onSelect={() => {
                      onAdd(def.key);
                      setOpen(false);
                    }}
                    className="cursor-pointer gap-2 text-xs"
                  >
                    {DefIcon != null && <DefIcon className="size-3.5 text-muted-foreground" />}
                    {def.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// FilterBar – main exported component
// ---------------------------------------------------------------------------

export function FilterBar({
  definitions,
  value,
  onChange,
  className,
  children,
}: FilterBarProps): React.ReactElement {
  // Track which filter keys are visible in the bar (added by user).
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  const [autoOpenKey, setAutoOpenKey] = useState<string | null>(null);

  // Merge: keys present in value (from parent) + keys user just added
  const activeKeys = useMemo(() => {
    const keys = new Set(Object.keys(value));
    for (const k of addedKeys) keys.add(k);
    return Array.from(keys);
  }, [value, addedKeys]);

  const activeKeySet = useMemo(() => new Set(activeKeys), [activeKeys]);

  const defByKey = useMemo(() => {
    const map = new Map<string, FilterDefinition>();
    for (const d of definitions) map.set(d.key, d);
    return map;
  }, [definitions]);

  useEffect(() => {
    if (autoOpenKey != null) {
      const t = setTimeout(() => setAutoOpenKey(null), 100);
      return () => clearTimeout(t);
    }
  }, [autoOpenKey]);

  // Build a default empty FilterValue for a given definition
  const makeEmptyValue = useCallback((def: FilterDefinition): FilterValue => {
    if (def.type === 'multi-select') {
      return { type: 'multi-select', values: [] };
    }
    if (def.type === 'single-select') {
      return { type: 'single-select', value: '' };
    }
    if (def.type === 'date-range') {
      // Custom-with-no-bounds is the inactive state, so a chip added and then
      // dismissed without a pick auto-removes like every other type.
      return { type: 'date-range', preset: 'custom', from: '', to: '' };
    }
    const operators = def.operators ?? getDefaultOperators(def.type);
    return { type: 'input', operator: operators[0]?.value ?? 'eq', value: '' };
  }, []);

  const handleAdd = useCallback(
    (key: string) => {
      if (!activeKeySet.has(key)) {
        setAddedKeys((prev) => new Set(prev).add(key));
        setAutoOpenKey(key);
      }
    },
    [activeKeySet],
  );

  const handleRemove = useCallback(
    (key: string) => {
      setAddedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      const next = { ...value };
      delete next[key];
      onChange(next);
    },
    [value, onChange],
  );

  const handleUpdate = useCallback(
    (key: string, filterValue: FilterValue) => {
      onChange({ ...value, [key]: filterValue });
    },
    [value, onChange],
  );

  const hasChips = activeKeys.length > 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}

      {children != null && (hasChips || definitions.length > 0) && (
        <Separator orientation="vertical" className="h-9" />
      )}

      {activeKeys.map((key) => {
        const def = defByKey.get(key);
        if (def == null) return null;
        const fv = value[key] ?? makeEmptyValue(def);
        return (
          <ActiveFilterChip
            key={key}
            definition={def}
            filterValue={fv}
            onUpdate={(val) => handleUpdate(key, val)}
            onRemove={() => handleRemove(key)}
            removable={def.removable !== false}
            defaultOpen={key === autoOpenKey}
          />
        );
      })}

      <AddFilterButton definitions={definitions} activeKeys={activeKeySet} onAdd={handleAdd} />
    </div>
  );
}
